import styled from 'styled-components';
import {Icon, Table} from '@forecast-it/design-system';
import type {DashboardTicket} from '../types/dashboardTicket';
import {
	DASHBOARD_BASE_DEFAULT_VISIBLE,
	DASHBOARD_COLUMN_LABELS,
	DASHBOARD_COLUMN_WIDTHS,
	type DashboardColumnKey,
} from '../constants/dashboardColumns';
import {DASHBOARD_CELL_ACCESSOR, dashboardTableRows, type DashboardTableRow} from '../utils/dashboardTableRows';
import {PriorityFlag} from '../../tickets/components/PriorityBadge';
import {slaPresentation} from '../../tickets/utils/slaPresentation';
import {ProjectColorDot} from './ProjectColorDot';

interface Props {
	tickets: readonly DashboardTicket[];
	/**
	 * Which columns are visible. When omitted the PRD's base default set is shown,
	 * so a caller that has no column menu still gets the intended table.
	 */
	visibleColumns?: ReadonlySet<DashboardColumnKey>;
	/** Distinguishes the tables of a grouped view in the DOM. */
	testId?: string;
}

// The table is wider than the page at its full column set, so it scrolls sideways
// rather than squeezing every cell. Base styles are the visible state, matching the
// queue's table.
const TableWrap = styled.div`
	display: block;
	overflow-x: auto;
`;

/**
 * The dashboard's ticket data table (PRD §6.5): all 22 fields of the §8.1 contract
 * as columns, of which the visible set is the reader's (see
 * `useDashboardColumnVisibility`).
 *
 * Every cell's text comes from `dashboardTableRows`, addressed through
 * `DASHBOARD_CELL_ACCESSOR` so the column, its sort and the CSV export can't drift
 * apart. Glyph-bearing columns pass a renderer to the design system's `icon` slot,
 * which draws *beside* the text — so nothing that already reads as text may go in
 * it, or the cell says the same thing twice.
 */
export function TicketDataTable({tickets, visibleColumns, testId = 'ticket-data-table'}: Props) {
	const visible = visibleColumns ?? DASHBOARD_BASE_DEFAULT_VISIBLE;
	const rows = dashboardTableRows(tickets);

	return (
		<TableWrap data-testid={testId}>
			<Table data={rows} getRowId={(row: DashboardTableRow) => row.rowId} width="100%" data-cy="ticket-data-table">
				{/* ID and Subject are not toggleable, so they carry no `visible` — the
				    PRD's field contract makes them the row's identity. */}
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.id}
					header={DASHBOARD_COLUMN_LABELS.id}
					width={DASHBOARD_COLUMN_WIDTHS.id}
					data-cy="ticket-id"
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.subject}
					header={DASHBOARD_COLUMN_LABELS.subject}
					ellipsis
					data-cy="ticket-subject"
				/>
				{/* Requesters are companies and contacts, not Forecast people, so there is
				    no profile picture to load — the avatar falls back to initials, which is
				    the PRD's hex avatar. Same as the queue's table. */}
				<Table.AvatarColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.requester}
					header={DASHBOARD_COLUMN_LABELS.requester}
					width={DASHBOARD_COLUMN_WIDTHS.requester}
					visible={visible.has('requester')}
					image={() => undefined}
					ellipsis
				/>
				<Table.AvatarColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.contact}
					header={DASHBOARD_COLUMN_LABELS.contact}
					width={DASHBOARD_COLUMN_WIDTHS.contact}
					visible={visible.has('contact')}
					image={() => undefined}
					ellipsis
				/>
				<Table.AvatarColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.assignee}
					header={DASHBOARD_COLUMN_LABELS.assignee}
					width={DASHBOARD_COLUMN_WIDTHS.assignee}
					visible={visible.has('assignee')}
					image={() => undefined}
					ellipsis
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.status}
					header={DASHBOARD_COLUMN_LABELS.status}
					width={DASHBOARD_COLUMN_WIDTHS.status}
					visible={visible.has('status')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.priority}
					header={DASHBOARD_COLUMN_LABELS.priority}
					width={DASHBOARD_COLUMN_WIDTHS.priority}
					visible={visible.has('priority')}
					icon={(row: DashboardTableRow) => <PriorityFlag priority={row.ticket.priority} />}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.channel}
					header={DASHBOARD_COLUMN_LABELS.channel}
					width={DASHBOARD_COLUMN_WIDTHS.channel}
					visible={visible.has('channel')}
				/>
				{/* The project's colour leads the cell and its name is the text — the dot
				    carries what a coloured chip would, without repeating the name the
				    column already prints. */}
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.project}
					header={DASHBOARD_COLUMN_LABELS.project}
					width={DASHBOARD_COLUMN_WIDTHS.project}
					visible={visible.has('project')}
					icon={(row: DashboardTableRow) => <ProjectColorDot project={row.ticket.project} />}
					ellipsis
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.tags}
					header={DASHBOARD_COLUMN_LABELS.tags}
					width={DASHBOARD_COLUMN_WIDTHS.tags}
					visible={visible.has('tags')}
					ellipsis
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.created}
					header={DASHBOARD_COLUMN_LABELS.created}
					width={DASHBOARD_COLUMN_WIDTHS.created}
					visible={visible.has('created')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.sla_state}
					header={DASHBOARD_COLUMN_LABELS.sla_state}
					width={DASHBOARD_COLUMN_WIDTHS.sla_state}
					visible={visible.has('sla_state')}
					icon={(row: DashboardTableRow) => {
						const {state, icon, iconColor} = slaPresentation(row.ticket.sla);
						// The glyph carries the SLA state; wrapping it in our own element is
						// what makes the state assertable, since the design-system Icon
						// renders no identifying attribute.
						return (
							<span data-testid="sla-icon" data-sla-state={state}>
								<Icon icon={icon} size="s" newColor={iconColor} />
							</span>
						);
					}}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.sla_due_at}
					header={DASHBOARD_COLUMN_LABELS.sla_due_at}
					width={DASHBOARD_COLUMN_WIDTHS.sla_due_at}
					visible={visible.has('sla_due_at')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.sla_breached_at}
					header={DASHBOARD_COLUMN_LABELS.sla_breached_at}
					width={DASHBOARD_COLUMN_WIDTHS.sla_breached_at}
					visible={visible.has('sla_breached_at')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.sla_paused_at}
					header={DASHBOARD_COLUMN_LABELS.sla_paused_at}
					width={DASHBOARD_COLUMN_WIDTHS.sla_paused_at}
					visible={visible.has('sla_paused_at')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.resolution_time}
					header={DASHBOARD_COLUMN_LABELS.resolution_time}
					width={DASHBOARD_COLUMN_WIDTHS.resolution_time}
					visible={visible.has('resolution_time')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.first_response_time}
					header={DASHBOARD_COLUMN_LABELS.first_response_time}
					width={DASHBOARD_COLUMN_WIDTHS.first_response_time}
					visible={visible.has('first_response_time')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.last_activity_at}
					header={DASHBOARD_COLUMN_LABELS.last_activity_at}
					width={DASHBOARD_COLUMN_WIDTHS.last_activity_at}
					visible={visible.has('last_activity_at')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.resolved_at}
					header={DASHBOARD_COLUMN_LABELS.resolved_at}
					width={DASHBOARD_COLUMN_WIDTHS.resolved_at}
					visible={visible.has('resolved_at')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.time_in_current_status}
					header={DASHBOARD_COLUMN_LABELS.time_in_current_status}
					width={DASHBOARD_COLUMN_WIDTHS.time_in_current_status}
					visible={visible.has('time_in_current_status')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.sla_policy_name}
					header={DASHBOARD_COLUMN_LABELS.sla_policy_name}
					width={DASHBOARD_COLUMN_WIDTHS.sla_policy_name}
					visible={visible.has('sla_policy_name')}
				/>
				<Table.TextColumn
					accessorKey={DASHBOARD_CELL_ACCESSOR.watchers_count}
					header={DASHBOARD_COLUMN_LABELS.watchers_count}
					width={DASHBOARD_COLUMN_WIDTHS.watchers_count}
					visible={visible.has('watchers_count')}
				/>
			</Table>
		</TableWrap>
	);
}
